import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { styled } from '@mui/material/styles';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { STEP_THREE_HEADER } from '../../../utils/const';
import IndividualListing from './IndividualListing';

const CheckboxAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  fontSize: '16px',
  height: 24,
  marginRight: theme.spacing(1.5),
  width: 24,
}));

const ProgressIcon = styled(CircularProgress)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
}));

const ConfirmAndSubmitStep = ({
  affiliate,
  batchActivate,
  currentSelection,
  fetchUniqueSelectors,
  fetchSelfAuthorizations,
  identifier,
  inactiveSelfAuthorizationsSelected,
  listType,
  locationId,
  productId,
  setSubmittingBatch,
  refreshSelfAuthorizationsListView,
  setSelfAuthorizationsPending,
  submittingBatch,
}) => {
  const { pathname } = useLocation();
  const isSelfAuthListViewEnabled = EvaluateFlag(process.env.REACT_APP_LD_SELF_AUTH_LIST_VIEW);
  // this ref assures we do not update the component's state (completed) after it has unmounted
  const isMounted = useRef(true);

  const [completed, setCompleted] = useState({});

  const completedProducts = Object.keys(completed);
  const percentComplete = parseInt(
    (completedProducts.length / inactiveSelfAuthorizationsSelected.length) * 100
  );
  const showProductIdentifier = !!locationId && listType !== 'baseProducts';

  useEffect(() => {
    const submitIndividualProduct = async item => {
      const { id } = item;
      const activationComplete = await batchActivate({
        affiliate,
        listType,
        locationId: locationId ?? id,
        productId: productId ?? id,
      });
      // this will let our submit function know not to do any state updates after we unmount
      if (isMounted.current) {
        setCompleted(c => ({ ...c, [id]: activationComplete }));
      }
      // return statement to resolve async function promise
      return activationComplete;
    };

    const viewBy = () => {
      if (pathname.includes('location')) return 'location';
      if (pathname.includes('product')) return 'product';
      return '';
    };

    const handleSubmit = async () => {
      setSubmittingBatch(true);
      const queryParams = {
        affiliate,
        [`${viewBy()}Id`]: currentSelection.id,
      };
      // map through array of products to submit
      const promiseArray = inactiveSelfAuthorizationsSelected.map(submitIndividualProduct);
      await Promise.all(promiseArray);
      setSubmittingBatch(false);

      // escape function if not in self auth, no need to fetch selectors
      if (!pathname.includes('self-authorizations')) return;

      // when on self auth
      // call to update selectors list, get new values for products
      // cards are updated locally through batchActivate function
      if (isSelfAuthListViewEnabled) {
        refreshSelfAuthorizationsListView();
      } else {
        fetchSelfAuthorizations(queryParams);
        setSelfAuthorizationsPending(true);
      }
      pathname.includes('location')
        ? fetchUniqueSelectors({ affiliate }, 'location', false)
        : fetchUniqueSelectors({ affiliate }, 'product', false);
    };
    if (inactiveSelfAuthorizationsSelected.length) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactiveSelfAuthorizationsSelected]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const progressOptions = () => {
    const boxStyles = {
      alignItems: 'center',
      bgcolor: 'surface.white',
      display: 'flex',
      flexDirection: 'row',
      height: '74px',
      justifyContent: 'flex-start',
      mt: 2,
      mx: 'auto',
      pl: 6,
    };
    if (submittingBatch) {
      return (
        <Box {...boxStyles}>
          <ProgressIcon color="secondary" size={24} />
          <Typography color="inherit" sx={{ pt: 0.5 }} variant="h6">
            Total Progress. . . {percentComplete}%
          </Typography>
        </Box>
      );
    }
    return (
      <Box {...boxStyles}>
        <CheckboxAvatar>
          <CheckRoundedIcon />
        </CheckboxAvatar>
        <Typography color="inherit" sx={{ pt: 0.5 }} variant="h6">
          All Setups Complete!
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box
        bgcolor="graphic.lighter"
        color="primary.dark"
        mt={1}
        pb={3}
        pt={2}
        px={3}
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.graphic.light}`,
        }}
        width="100%"
      >
        <Typography color="inherit">{`${STEP_THREE_HEADER}${identifier}`}</Typography>
        {progressOptions()}
      </Box>
      {inactiveSelfAuthorizationsSelected.map(({ id, nameShort, proprietary }) => {
        const completedListing = completed[id];
        return (
          <IndividualListing
            completedListing={completedListing}
            key={`IndividualListing${id}`}
            nameShort={nameShort}
            proprietary={proprietary}
            showProductIdentifier={showProductIdentifier}
          />
        );
      })}
    </>
  );
};

ConfirmAndSubmitStep.propTypes = {
  affiliate: shape({
    companyCustomerIdName: string.isRequired,
    dataCompanyId: number.isRequired,
  }).isRequired,
  batchActivate: func.isRequired,
  currentSelection: shape({}).isRequired,
  fetchSelfAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  identifier: string.isRequired,
  inactiveSelfAuthorizationsSelected: arrayOf(shape({})).isRequired,
  listType: string.isRequired,
  locationId: number,
  productId: number,
  refreshSelfAuthorizationsListView: func.isRequired,
  setSelfAuthorizationsPending: func.isRequired,
  setSubmittingBatch: func.isRequired,
  submittingBatch: bool.isRequired,
};

ConfirmAndSubmitStep.defaultProps = {
  locationId: undefined,
  productId: undefined,
};

export default ConfirmAndSubmitStep;
